import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  DataTable,
  LoadingState,
  EmptyState,
  Alert,
  Badge,
  Card,
  FilterBar,
  FormField,
  Input,
  Select,
} from '@sakhtar/design-system/react';
import { listOperatorOrganizations, type OrganizationSummary } from '../../lib/operatorApi.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { OperatorPage } from './OperatorShell.js';
import { OrgAvatar, WithAvatar } from './OperatorAvatars.js';

export const LIFECYCLE_CONFIG: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }
> = {
  active: { label: 'فعال', tone: 'success' },
  provisioning: { label: 'در حال راه‌اندازی', tone: 'warning' },
  readonly: { label: 'فقط‌خواندنی', tone: 'default' },
  suspended: { label: 'معلق', tone: 'danger' },
  archived: { label: 'بایگانی‌شده', tone: 'default' },
  failed: { label: 'راه‌اندازی ناموفق', tone: 'danger' },
};

export function OrganizationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<readonly OrganizationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    void listOperatorOrganizations()
      .then(setRows)
      .catch(() => setError('بارگذاری سازمان‌ها ناموفق بود.'));
  }, []);

  const stats = useMemo(() => {
    if (!rows) return null;
    const total = rows.length;
    const active = rows.filter((r) => r.lifecycleStatus === 'active').length;
    const suspended = rows.filter((r) => r.lifecycleStatus === 'suspended').length;
    const trial = rows.filter(
      (r) => r.commercial.mode === 'trial' || Boolean(r.commercial.trialEndsAt),
    ).length;
    return { total, active, suspended, trial };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    return rows.filter((row) => {
      if (statusFilter !== 'all' && row.lifecycleStatus !== statusFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matchesName = row.name.toLowerCase().includes(query);
        const matchesSlug = row.slug.toLowerCase().includes(query);
        if (!matchesName && !matchesSlug) {
          return false;
        }
      }
      return true;
    });
  }, [rows, statusFilter, searchQuery]);

  return (
    <OperatorPage
      title="مدیریت سازمان‌ها"
      actions={
        <Button type="button" onClick={() => navigate('/internal/admin/organizations/new')}>
          + ساخت سازمان جدید
        </Button>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {/* خلاصه وضعیت آماری سازمان‌ها */}
      {stats ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <Card title="کل سازمان‌ها">
            <span style={{ fontSize: 24, fontWeight: 'bold' }}>{stats.total}</span>
          </Card>
          <Card title="سازمان‌های فعال">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#10b981' }}>
              {stats.active}
            </span>
          </Card>
          <Card title="دوره‌های آزمایشی">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.trial}
            </span>
          </Card>
          <Card title="سازمان‌های معلق">
            <span style={{ fontSize: 24, fontWeight: 'bold', color: '#ef4444' }}>
              {stats.suspended}
            </span>
          </Card>
        </div>
      ) : null}

      {/* فیلترها و جستجو */}
      {rows !== null && rows.length > 0 ? (
        <Card>
          <FilterBar>
            <FormField label="جستجو">
              <Input
                placeholder="نام یا نامک سازمان..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </FormField>
            <FormField label="وضعیت چرخه عمر">
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">همه وضعیت‌ها</option>
                <option value="active">فعال</option>
                <option value="provisioning">در حال راه‌اندازی</option>
                <option value="suspended">معلق</option>
                <option value="readonly">فقط‌خواندنی</option>
                <option value="failed">راه‌اندازی ناموفق</option>
              </Select>
            </FormField>
            {searchQuery || statusFilter !== 'all' ? (
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                پاک کردن فیلترها
              </Button>
            ) : null}
          </FilterBar>
        </Card>
      ) : null}

      {rows === null ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="هنوز سازمانی ثبت نشده است." />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="هیچ سازمانی با این مشخصات یافت نشد." />
      ) : (
        <DataTable
          columns={[
            {
              key: 'name',
              header: 'سازمان',
              render: (row) => (
                <WithAvatar avatar={<OrgAvatar slug={row.slug} name={row.name} size={36} />}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <strong style={{ fontSize: 14 }}>{row.name}</strong>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      ایجاد: {row.createdAt ? displayJalaliDate(row.createdAt) : '—'}
                    </span>
                  </div>
                </WithAvatar>
              ),
            },
            {
              key: 'slug',
              header: 'نامک / شناسه',
              render: (row) => (
                <code
                  dir="ltr"
                  style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    background: 'var(--sk-surface-inset, #f3f4f6)',
                    fontSize: 12,
                  }}
                >
                  {row.slug}
                </code>
              ),
            },
            {
              key: 'lifecycle',
              header: 'چرخه عمر',
              render: (row) => {
                const conf = LIFECYCLE_CONFIG[row.lifecycleStatus] ?? {
                  label: row.lifecycleStatus,
                  tone: 'default' as const,
                };
                return <Badge tone={conf.tone}>{conf.label}</Badge>;
              },
            },
            {
              key: 'plan',
              header: 'طرح تجاری',
              render: (row) => (
                <span style={{ fontSize: 13 }}>{row.commercial.planDisplayName || '—'}</span>
              ),
            },
            {
              key: 'trial',
              header: 'پایان آزمایشی',
              render: (row) =>
                row.commercial.trialEndsAt ? displayJalaliDate(row.commercial.trialEndsAt) : '—',
            },
            {
              key: 'seats',
              header: 'صندلی کاربر',
              render: (row) => (
                <span dir="ltr" style={{ fontSize: 13 }}>
                  {row.commercial.seatUsed} / {row.commercial.seatLimit}
                </span>
              ),
            },
            {
              key: 'open',
              header: 'عملیات',
              render: (row) => (
                <Link
                  to={`/internal/admin/organizations/${row.slug}`}
                  style={{
                    color: 'var(--sk-primary, #2563eb)',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  مشاهده و مدیریت ←
                </Link>
              ),
            },
          ]}
          rows={[...filteredRows]}
          rowKey={(row) => row.slug}
        />
      )}
      <p style={{ color: 'var(--sk-fg-muted)', fontSize: 13, marginTop: 16 }}>
        این کنسول داخلی کنترل‌پلتفرم است و به داده‌های محرمانه عملیاتی کسب‌وکار مشتری دسترسی ندارد.
      </p>
    </OperatorPage>
  );
}
